import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { frequencyLabel } from './plan-view.js';
import type { IntervalHistoryEntry } from './types.js';

/** Geçmiş durumunun rozet tonu (`status-tone.ts` eşlemesiyle aynı renk dili). */
const STATUS_TONE = {
  effective: 'success',
  pending: 'warning',
  returned: 'danger',
  sent: 'info',
  rejected: 'danger',
} as const satisfies Record<IntervalHistoryEntry['status'], string>;

/**
 * Job Card'ın aralık geçmişi (ADR 0104 §7): gemide yazılmış değişiklikler kaynağıyla (Ship /
 * Office) ve gemide henüz uygulanmamış ya da uygulanamamış ofis kararları, yeniden eskiye. Geçmiş
 * boşsa çizilmez.
 */
export function IntervalHistory({ entries }: { entries: readonly IntervalHistoryEntry[] }) {
  const { t, i18n } = useTranslation();
  if (entries.length === 0) return null;
  const language = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.intervalHistory.${key}`, values);
  return (
    <fieldset className={classes.fieldset}>
      <legend>{k('title')}</legend>
      <ul className={work.detailList} aria-label={k('title')}>
        {entries.map((entry) => {
          const interval = frequencyLabel(entry.newInterval, t, language);
          const previous = entry.previousInterval
            ? frequencyLabel(entry.previousInterval, t, language)
            : null;
          const meta = [
            formatDay(entry.at.slice(0, 10)),
            entry.byName ?? (entry.source === 'office' ? k('office') : null),
            // O günkü rol (DNV-CP-0206 Sec.2 [4.6] b; ADR 0133 §2)
            entry.byRole ?? null,
            t(`pms.work.bases.${entry.intervalBasis}`, { defaultValue: entry.intervalBasis }),
            entry.classReference ? k('classReference', { ref: entry.classReference }) : null,
          ].filter((part): part is string => Boolean(part));
          return (
            <li key={`${entry.kind}-${entry.id}`}>
              <span className={work.detailListMain}>
                <strong>{previous ? `${previous} → ${interval}` : interval}</strong>
                <span className={work.note}>{meta.join(' · ')}</span>
                <span className={work.note}>{entry.reason}</span>
                {entry.rejection ? (
                  <span className={work.note} role="note">
                    {t(`pms.errors.${entry.rejection.code}`, {
                      defaultValue: entry.rejection.reason ?? entry.rejection.code,
                    })}
                  </span>
                ) : null}
              </span>
              <span className={work.statusCell}>
                <StatusBadge tone="neutral" variant="dot">
                  {k(`sources.${entry.source}`)}
                </StatusBadge>
                <StatusBadge tone={STATUS_TONE[entry.status]}>
                  {k(`statuses.${entry.status}`)}
                </StatusBadge>
              </span>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
